// Pure scope helpers for the Tenant Portal Ledger Download dropdown.

import { PAYMENTS } from 'portal-modules/payments/constants/urls'
import {
  LEDGER_TRANSACTION_TYPES,
  PAYMENT_STATUS_TYPES_BY_ID,
} from 'portal-modules/payments/constants/types'

export const LEDGER_SCOPES = Object.freeze({
  FULL: 'full',
  PAYMENTS: 'payments',
  CHARGES: 'charges',
  INVOICES: 'invoices',
})

// Word-boundary match per spec — "Invoice #4501" matches, "invoiced" doesn't.
const INVOICE_LABEL_REGEX = /\binvoice\b/i

const buildInvoiceMatchTarget = row => {
  if (!row || typeof row !== 'object') return ''
  return `${row.type ?? ''} ${row.label ?? ''}`.trim()
}

const isPaymentRow = row =>
  row?.transaction_type === LEDGER_TRANSACTION_TYPES.PAYMENT.value

const isChargeRow = row =>
  row?.transaction_type === LEDGER_TRANSACTION_TYPES.CHARGE.value

const isInvoiceRow = row =>
  INVOICE_LABEL_REGEX.test(buildInvoiceMatchTarget(row))

/**
 * Filter ledger rows by the given scope.
 *
 * Reference-stability note: the FULL / default case intentionally returns
 * the input `rows` array reference unchanged (no copy) so downstream
 * `React.memo` / `shallowEqual` checks can short-circuit re-renders when
 * the ledger has not changed. The other branches return a new array via
 * `Array#filter`, which is the standard semantic. Callers that need to
 * mutate the result must copy it themselves.
 *
 * @param {Array<Object>} rows - ledger transaction rows
 * @param {string} scope - one of `LEDGER_SCOPES` values
 * @returns {Array<Object>} filtered rows; new array for non-FULL scopes,
 *   same reference as `rows` for FULL.
 */
export const filterLedgerByScope = (rows, scope) => {
  if (!Array.isArray(rows)) return []
  switch (scope) {
    case LEDGER_SCOPES.PAYMENTS:
      return rows.filter(isPaymentRow)
    case LEDGER_SCOPES.CHARGES:
      return rows.filter(isChargeRow)
    case LEDGER_SCOPES.INVOICES:
      return rows.filter(isInvoiceRow)
    case LEDGER_SCOPES.FULL:
    default:
      return rows
  }
}

// Order matters: more specific paths must be checked before the LEDGER root.
export const getActiveScopeFromLocation = (pathname = '') => {
  const path = typeof pathname === 'string' ? pathname : ''
  if (path.includes(PAYMENTS.LEDGER.PAYMENTS)) return LEDGER_SCOPES.PAYMENTS
  if (path.includes(PAYMENTS.LEDGER.CHARGES)) return LEDGER_SCOPES.CHARGES
  if (path.includes(PAYMENTS.LEDGER.INVOICE)) return LEDGER_SCOPES.INVOICES
  return LEDGER_SCOPES.FULL
}

// Payment complete / pending predicates — replicated from the on-screen
// PaymentsLedger connect so the exported Payments sections contain the
// exact rows tenants see in the "Complete" / "Pending" groups.
const isCompletePayment = ({ status_type_id, status } = {}) =>
  PAYMENT_STATUS_TYPES_BY_ID.CAPTURED == status_type_id ||
  PAYMENT_STATUS_TYPES_BY_ID.RETURNED === status_type_id ||
  PAYMENT_STATUS_TYPES_BY_ID.RECEIVED === status_type_id ||
  PAYMENT_STATUS_TYPES_BY_ID.VOIDED === status_type_id ||
  PAYMENT_STATUS_TYPES_BY_ID.REVERSED === status_type_id ||
  status?.trim() === 'Declined'

// The on-screen pending filter ends with a truthy `indexOf(status_type_id)`
// guard. Because PENDING sits at index 0 of PAYMENT_STATUS_TYPES_BY_ID, that
// guard is falsy for PENDING rows (and for a missing id, indexOf === -1, which
// stays truthy). The `!== 0` check reproduces that exact truthiness as a
// boolean so the exported Pending section contains the same rows as the screen.
const isPendingPayment = ({ status_type_id, status } = {}) =>
  PAYMENT_STATUS_TYPES_BY_ID.CAPTURED !== status_type_id &&
  PAYMENT_STATUS_TYPES_BY_ID.RETURNED !== status_type_id &&
  PAYMENT_STATUS_TYPES_BY_ID.RECEIVED !== status_type_id &&
  PAYMENT_STATUS_TYPES_BY_ID.DELETED !== status_type_id &&
  PAYMENT_STATUS_TYPES_BY_ID.VOIDED !== status_type_id &&
  PAYMENT_STATUS_TYPES_BY_ID.REVERSED !== status_type_id &&
  status?.trim() !== 'Declined' &&
  Object.values(PAYMENT_STATUS_TYPES_BY_ID).indexOf(status_type_id) !== 0

// Builds the section list the export pipeline serialises for a scope. Each
// section is `{ title?, rows }`; sections with no rows are dropped. The
// grouped scopes (Payments) mirror the on-screen section headers, while
// Full / Charges / Invoices are a single untitled section.
//
// Charges is posted-only (the ledger charge rows); scheduled charges are
// intentionally excluded from the export.
export const buildLedgerExportSections = ({
  scope,
  ledgerRows = [],
  customerInvoices = [],
} = {}) => {
  const rows = Array.isArray(ledgerRows) ? ledgerRows : []
  switch (scope) {
    case LEDGER_SCOPES.PAYMENTS: {
      const paymentRows = rows.filter(isPaymentRow)
      const pending = paymentRows.filter(isPendingPayment)
      const complete = paymentRows.filter(isCompletePayment)
      const sections = []
      if (pending.length) {
        sections.push({ title: __('Pending Payments'), rows: pending })
      }
      if (complete.length) {
        // Match the screen: the "Complete Payments" heading only appears
        // once a Pending section precedes it.
        sections.push({
          title: pending.length ? __('Complete Payments') : undefined,
          rows: complete,
        })
      }
      return sections
    }
    case LEDGER_SCOPES.CHARGES:
      return [{ rows: rows.filter(isChargeRow) }]
    case LEDGER_SCOPES.INVOICES:
      return [{ rows: Array.isArray(customerInvoices) ? customerInvoices : [] }]
    case LEDGER_SCOPES.FULL:
    default:
      return [{ rows }]
  }
}

// Total exportable row count for a scope — the sum across its sections.
export const countLedgerExportRows = sections =>
  (Array.isArray(sections) ? sections : []).reduce(
    (total, section) =>
      total + (Array.isArray(section?.rows) ? section.rows.length : 0),
    0,
  )
